/**
 * @module hooks/useWaveformCache
 * @author Charles Blais <charles.blais@canada.ca>
 *
 * Use waveform cache
 */

import moment from 'moment';

import { AxiosError } from 'axios';

import useAxios from 'axios-hooks';

import { useState, useEffect, useCallback } from 'react';

import { decode } from "@msgpack/msgpack";

import { WaveformTrace } from '../typings/waveform-plot';

type Props = {
  initialUrl: string,
  initialSNCL?: string,
  initialPeriod?: string,
  initialTrace?: WaveformTrace,
}


type ReturnProps = {
  trace?: WaveformTrace,
  loading: Boolean,
  error: AxiosError<any, any> | null,
  setUrl: (value: string) => void,
  setSNCL: (value: string) => void,
  setPeriod: (Value: string) => void,
  refetch: () => void,
}

/**
 * Convert streams trace meta datetime to moment object for simplicit
 * in other routines.
 * @param {list} streams
 * @returns streams with altered information
 */
const convertData = (
  data: any
): WaveformTrace => {
  const t: any = decode(data)
  t.header.starttime = moment.utc(t.header.starttime);
  if('endtime' in t.header){
    t.header.endtime = moment.utc(t.header.endtime);
  } else {
    t.header.endtime = moment.utc(t.header.starttime).add(
      parseFloat(t.header.delta) * (t.data.length - 1), 's');
  }
  return t
};


/**
 * Get the URL
 * @param url 
 * @param sncl 
 * @param period 
 * @returns 
 */
const getUrl = (
  url: string,
  sncl: string,
  period: string,
): string => {
  return `${url}/${sncl}.${period}.msgpack`
}


/**
 * React Hook
 * ==========
 *
 * Query the web cache service which contains a series of
 * msgpack message.
 */
const useWaveformCache = ({
  initialUrl = '',
  initialSNCL = '',
  initialPeriod = '',
  initialTrace = undefined,
}: Props): ReturnProps => {
  const [{ data, loading, error }, fetch] = useAxios(
    {
      url: getUrl(initialUrl, initialSNCL, initialPeriod),
      responseType: 'arraybuffer',
    },{
      manual: initialUrl.length === 0 && initialPeriod.length === 0 && initialSNCL.length === 0
    });

  const [ url, setUrl ] = useState(initialUrl);

  const [ sncl, setSNCL ] = useState(initialSNCL);

  const [ period, setPeriod ] = useState(initialPeriod);

  const [ trace, setTrace ] = useState(initialTrace);


  const refetch = useCallback(() => {
    if(sncl.length !== 0 && period.length !== 0){
      console.log(`Fetching data for ${sncl} ${period}`)
      fetch({
        url: getUrl(url, sncl, period),
        responseType: 'arraybuffer',
      })
    }
  }, [fetch, url, sncl, period])

  useEffect(() => {
    refetch()
  }, [refetch, fetch, url, sncl, period])

  useEffect(() => {
    if(data) setTrace(convertData(data))
  }, [data])

  return { trace, loading, error, setUrl, setSNCL, setPeriod, refetch };
};

export default useWaveformCache;